import { useEffect, useState } from 'react'
import { ShieldCheck, User, X } from 'lucide-react'
import { patientsApi, type PatientResponse } from '../lib/api'
import { VERIFICATION_TIERS } from '../lib/verificationTiers'
import { useLang, t, fmt, statusLabel, sexLabel } from '../lib/i18n'

/**
 * Patient profile view (Sprint 2 — Minimum Registry).
 *
 * Fetches a patient by ID via GET /api/v1/patients/{id} and renders the
 * identity, NHI, demographics and verification tier.
 */

export function PatientProfile({
  patientId,
  onClose,
}: {
  patientId: string
  onClose: () => void
}) {
  const [patient, setPatient] = useState<PatientResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    patientsApi
      .get(patientId)
      .then((res) => {
        if (!cancelled) setPatient(res)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('profile.failed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [patientId])

  const tier = patient ? VERIFICATION_TIERS[patient.verification_tier] : null

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-title"
      className="animate-pop-in rounded-xl border border-emerald-100 bg-white p-5 shadow-lift"
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 id="profile-title" className="text-lg font-semibold text-stone-950">
          {t('profile.title')}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('profile.closeAria')}
          className="grid h-9 w-9 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:bg-stone-50 active:scale-90"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading && (
        <div className="space-y-3">
          <div className="skeleton h-6 w-48" />
          <div className="skeleton h-4 w-64" />
          <div className="skeleton h-4 w-40" />
        </div>
      )}

      {error && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800" role="alert">
          {error}
        </p>
      )}

      {patient && tier && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-xl bg-primary-50 text-primary-700">
              <User className="h-7 w-7" />
            </div>
            <div>
              <div className="text-xl font-semibold text-stone-950">
                {patient.person.name_latin || patient.person.name_ar || t('profile.unnamed')}
              </div>
              {patient.person.name_ar && patient.person.name_latin && (
                <div className="text-sm text-stone-500">{patient.person.name_ar}</div>
              )}
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                <ShieldCheck className="h-3.5 w-3.5" />
                {fmt(t('profile.nhi'), { nhi: patient.person.nhi })}
              </div>
            </div>
          </div>

          <dl className="grid gap-3 sm:grid-cols-2">
            <ProfileField label={t('profile.dob')} value={patient.person.dob ?? '—'} />
            <ProfileField label={t('profile.sex')} value={patient.person.sex ? sexLabel(patient.person.sex) : '—'} />
            <ProfileField label={t('profile.phone')} value={patient.person.phone ?? '—'} />
            <ProfileField label={t('profile.mrn')} value={patient.mrn} />
            <ProfileField label={t('profile.facility')} value={patient.facility_id} />
            <ProfileField label={t('profile.status')} value={statusLabel(patient.status)} />
          </dl>

          <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
              <ShieldCheck className="h-4 w-4 text-primary-700" />
              {fmt(t('profile.tier'), { n: patient.verification_tier, name: t(`tier.name${tier.level}`) })}
            </div>
            <p className="mt-1 text-xs leading-5 text-stone-500">
              {t(`tier.desc${tier.level}`)}
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-stone-100 bg-white p-3">
      <dt className="text-xs font-medium text-stone-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-stone-900">{value}</dd>
    </div>
  )
}
